import { formatISOToPattern, localDateToISO } from '@indeed/jspl-foundations';
import type {
    AnswerSmartApplyApplicationStateInput,
    HierarchicalAnswerValueSmartApplyApplicationStateInput,
    QuestionAndAnswerSmartApplyApplicationStateInput,
    ScreenerQuestionContextFormField,
    ScreenerQuestionContextQuestion,
    ScreenerQuestionContextQuestionFormat,
    SmartApplyScreenerQuestionType,
} from '../../../__generated__/graphql-base-types';
import type { SavedScreenerAnswerFragment } from './__generated__/QuestionsPage.generated';

/**
 * A FILE screener answer, carrying the display name and the persist identifier separately.
 *
 * The flat `string[]` other question types use cannot hold both the filename to show and the
 * `fileToken` to persist, so FILE answers use this richer shape (mirrors mosaic's `UploadedFile`).
 * `fileToken` is absent while an upload is in flight or has failed; only tokened entries serialize.
 */
export interface ScreenerFileAnswer {
    /** Original filename, shown in the field and the collapsed preview. */
    name: string;
    /**
     * Opaque token minted by the upload / carried by a saved answer; the persist URI is built from
     * it. The only thing that makes a FILE answer persistable — an entry without it (an in-flight or
     * failed upload, or a name-only context prefill) is dropped on save.
     */
    fileToken?: string;
    contentType?: string;
    contentHash?: string;
    downloadHash?: string;
    /** True while the file is uploading — renders the placeholder spinner and gates Next. */
    uploading?: boolean;
    /** True when the upload failed, so the field can surface it. */
    error?: boolean;
}

/**
 * The page's per-question answer value. FILE questions carry `ScreenerFileAnswer[]`; every other
 * type keeps the flat `string[]`. Mirrors mosaic's `Answer` union.
 */
export type AnswerValue = string[] | ScreenerFileAnswer[];

/**
 * Flattens any answer value to the display strings the collapsed preview and validation share:
 * a FILE answer surfaces its filenames, everything else is already `string[]`.
 */
export const getDisplayValues = (
    question: ScreenerQuestionContextQuestion,
    value: AnswerValue | undefined,
): string[] =>
    question.type === 'FILE'
        ? ((value as ScreenerFileAnswer[] | undefined) ?? []).map((file) => file.name)
        : ((value as string[] | undefined) ?? []);

/**
 * Converts a DATE question's `min`/`max` to the ISO 8601 that jspl's `DateQuestion` expects.
 *
 * The API sends the bounds in the question's `outputDatePattern` — the same format as the answer
 * itself — so they need converting even though the value does not. Smart Apply's questions module
 * does the same before handing them to `DateQuestion`. Returns undefined for an unparseable bound,
 * which leaves that end unbounded rather than rejecting every date.
 */
export const dateBoundToISO = (
    bound: string | null | undefined,
    outputDatePattern: string | null | undefined,
): string | undefined =>
    bound
        ? localDateToISO(bound, undefined, outputDatePattern || undefined) || undefined
        : undefined;

/** Question types that accept an answer. The rest are presentational or handled separately. */
const ANSWERABLE_TYPES = new Set<SmartApplyScreenerQuestionType>([
    'DATE',
    'FILE',
    'HIERARCHICAL',
    'MULTISELECT',
    'SELECT',
    'TEXT',
    'TEXTAREA',
]);

export const isAnswerable = (type: SmartApplyScreenerQuestionType): boolean =>
    ANSWERABLE_TYPES.has(type);

/**
 * Flattens the polymorphic form field into the page's `AnswerValue` — `string[]` for every type
 * except FILE, which becomes `ScreenerFileAnswer[]` (see the FILE case). `getDisplayValues` reduces
 * either to the display strings AnswerPreview and the collapsed preview consume.
 */
export const getAnswerValues = (formField: ScreenerQuestionContextFormField): AnswerValue => {
    switch (formField.__typename) {
        case 'ScreenerQuestionContextSingleValuedFormField':
            return formField.textValue ? [formField.textValue] : [];
        case 'ScreenerQuestionContextMultiValuedFormField':
            return formField.values.filter(Boolean);
        case 'ScreenerQuestionContextHierarchicalFormField':
            return formField.entries.map((entry) => entry.entryValue).filter(Boolean);
        // A pre-allocated slot carries only a display name (a bare filename), not a persistable
        // token — so it renders by name but is dropped on save unless the jobseeker (re)uploads,
        // rather than round-tripping the filename as a WebUrl (the IAFE-3026 save-breaking bug). A
        // genuinely-saved file arrives instead via getSavedAnswerValues below, carrying its token.
        case 'ScreenerQuestionContextFileFormField':
            return formField.fileSlots
                .filter((slot) => slot.slotValue)
                .map((slot) => ({ name: slot.slotValue }));
        // MULTIFIELD answers live in MultiFieldAnswerSmartApplyApplicationState, not here.
        case 'ScreenerQuestionContextMultiFieldFormField':
        default:
            return [];
    }
};

/**
 * Question prompt with the required marker, for the collapsed preview only.
 *
 * The editable field gets its indicator from jspl's `showRequiredIndicator`, but
 * `CollapsibleQuestion` renders the label itself when collapsed and has no notion of required —
 * so the asterisk is appended here to keep both states showing the same marker.
 */
export const getQuestionLabel = (question: ScreenerQuestionContextQuestion): string =>
    question.required ? `${question.labelHtml} *` : question.labelHtml;

/** Seeds the page's answer state, keyed by the question `name` the form fields use. */
export const buildInitialAnswers = (
    questions: ScreenerQuestionContextQuestion[],
): Record<string, AnswerValue> =>
    Object.fromEntries(questions.map((q) => [q.name, getAnswerValues(q.formField)]));

/**
 * Converts a saved single-text answer from the backend format the save writes back into the
 * jspl field's display format, so it round-trips cleanly. DATE saves as ISO but `DateQuestion`
 * (and re-save) expect the employer's `outputDatePattern`; numeric TEXT saves with a "." decimal
 * but `NumberQuestion` (and `normalizeNumericAnswer` on re-save) expect the locale
 * `decimalSeparator`. Without this, a re-saved date would parse to "" (wiping it) and a re-saved
 * number would be corrupted when the thousand separator is ".".
 */
const savedTextToDisplay = (question: ScreenerQuestionContextQuestion, value: string): string => {
    if (question.type === 'DATE') {
        // formatISOToPattern returns "" without a pattern, which would wipe the value — so only
        // convert when the employer configured one (the same field the whole page keys date I/O on).
        return question.outputDatePattern
            ? formatISOToPattern(value, question.outputDatePattern)
            : value;
    }
    if (isNumericQuestion(question)) {
        const decimalSeparator = question.decimalSeparator ?? '.';
        return decimalSeparator === '.' ? value : value.replace('.', decimalSeparator);
    }
    return value;
};

/**
 * Answer `__typename` values that intentionally reach the switch `default`. The generated union
 * members carry an optional `__typename`, so once every concrete answer type has a case, `undefined`
 * is the only value left — a newly generated member adds a concrete literal that is no longer
 * assignable here, failing the guard at compile time (mirrors resumePrefill.ts).
 */
type UnsupportedSavedAnswerType = undefined;

const assertUnsupportedSavedAnswerType = (value: UnsupportedSavedAnswerType): void => {
    // Compile-time only: the value is intentionally unused at runtime.
    void value;
};

/** A saved FILE object (SmartApplyApplicationState) mapped to the answer model, keeping the token. */
const toFileAnswer = (file: {
    name: string;
    fileToken: string;
    contentType: string;
    contentHash: string;
    downloadHash: string;
}): ScreenerFileAnswer => ({
    name: file.name,
    fileToken: file.fileToken,
    contentType: file.contentType,
    contentHash: file.contentHash,
    downloadHash: file.downloadHash,
});

/** Flattens a previously-saved answer (SmartApplyApplicationState) into the page's answer value. */
export const getSavedAnswerValues = (
    question: ScreenerQuestionContextQuestion,
    answer: SavedScreenerAnswerFragment['answer'],
): AnswerValue => {
    if (!answer) return [];
    switch (answer.__typename) {
        case 'TextAnswerSmartApplyApplicationState':
            return answer.value ? [savedTextToDisplay(question, answer.value)] : [];
        case 'MultiTextAnswerSmartApplyApplicationState':
            return answer.valueList.map((entry) => entry.value).filter(Boolean);
        case 'HierarchicalAnswerSmartApplyApplicationState':
            return answer.valueList.map((entry) => entry.value).filter(Boolean);
        // Keep both the name (for display) and the token (for a clean re-save round-trip).
        case 'FileAnswerSmartApplyApplicationState':
            return answer.file.name ? [toFileAnswer(answer.file)] : [];
        case 'MultiFileAnswerSmartApplyApplicationState':
            return answer.files
                .filter((entry) => entry.file.name)
                .map((entry) => toFileAnswer(entry.file));
        // MULTIFIELD answers are rendered read-only and not overlaid (entry editor is a follow-up).
        case 'MultiFieldAnswerSmartApplyApplicationState':
            return [];
        default:
            // Only `undefined` should reach here; a newly generated concrete answer member fails
            // this call at compile time, prompting an explicit case above.
            assertUnsupportedSavedAnswerType(answer.__typename);
            return [];
    }
};

/**
 * The answers the form loads with: the `smartApplyContext` `formField` prefills overlaid with any
 * answers the jobseeker previously saved on the draft (`SmartApplyApplicationState`). Matched by
 * `question.id`, a saved answer wins over the prefill — mirroring the mosaic module's answerMap.
 * Keyed by `question.name`, like the rest of the page's answer state.
 */
export const buildLoadedAnswers = (
    questions: ScreenerQuestionContextQuestion[],
    savedScreener: readonly SavedScreenerAnswerFragment[],
): Record<string, AnswerValue> => {
    const savedByQuestionId = new Map(savedScreener.map((item) => [item.questionId, item.answer]));
    return Object.fromEntries(
        questions.map((question) => {
            const saved = savedByQuestionId.get(question.id);
            return [
                question.name,
                saved ? getSavedAnswerValues(question, saved) : getAnswerValues(question.formField),
            ];
        }),
    );
};

/** Free-text answers only count once they hold something other than whitespace. */
const FREE_TEXT_TYPES = new Set<SmartApplyScreenerQuestionType>(['TEXT', 'TEXTAREA']);

/** Single definition of "this question has an answer", shared by every consumer. */
export const isAnswered = (
    question: ScreenerQuestionContextQuestion,
    values: AnswerValue | undefined,
): boolean => {
    const answer = values ?? [];
    // A FILE counts as answered once it has a file that is not mid-upload — a resolved upload, a
    // saved answer, or an already-attached context prefill (shown by name). Only an in-flight upload
    // placeholder does not count, so Next stays gated until it resolves. (Whether an entry is
    // *persistable* is a separate concern handled in buildScreenerAnswerInput, which drops entries
    // without a token so a bare-filename prefill is never sent as a WebUrl.)
    if (question.type === 'FILE') {
        return (answer as ScreenerFileAnswer[]).some((file) => !file.uploading);
    }
    return FREE_TEXT_TYPES.has(question.type)
        ? (answer as string[]).some((value) => value.trim() !== '')
        : answer.length > 0;
};

/**
 * Whether any screener question was AI-prefilled — the signal that gates the server's AI-autofill
 * disclaimer. Reuses the page's own "is this answered" logic against each question's `formField` (the
 * prefill, never the seeker's saved answers), so it matches the questions the seeker saw collapsed.
 * Shared by `useShowAiAutofillDisclaimer` (ReviewPage) and ConfirmationPage so they can't diverge.
 */
export const hasPrefilledScreenerAnswer = (questions: ScreenerQuestionContextQuestion[]): boolean =>
    questions.some(
        (question) =>
            isAnswerable(question.type) &&
            isAnswered(question, getAnswerValues(question.formField)),
    );

/** True when a FILE answer has at least one entry that can actually be persisted (has a token). */
const hasPersistableFileAnswer = (value: AnswerValue | undefined): boolean =>
    ((value as ScreenerFileAnswer[] | undefined) ?? []).some((file) => Boolean(file.fileToken));

/**
 * True while a required question has no answer, read from page state rather than jspl.
 *
 * This is the one thing jspl cannot tell us: its validators enforce `required` perfectly well, but
 * only for fields that are mounted, and a collapsed `CollapsibleQuestion` mounts nothing. So the
 * page uses this to hold such a question open — at which point its field registers and jspl takes
 * over, which is why nothing gates the CTAs on it.
 *
 * A required FILE needs a *persistable* file (a token), not merely a name: a bare-filename context
 * prefill cannot be saved, so the question is held open to prompt an upload (and, in turn, is not
 * sent as an empty required answer by `buildScreenerAnswers`).
 */
export const isMissingRequiredAnswer = (
    question: ScreenerQuestionContextQuestion,
    answers: Partial<Record<string, AnswerValue>>,
): boolean => {
    if (!question.required || !isAnswerable(question.type)) return false;
    if (question.type === 'FILE') return !hasPersistableFileAnswer(answers[question.name]);
    return !isAnswered(question, answers[question.name]);
};

/**
 * Adds `draftId` to the comma-separated `failedDraftIds` URL search param (the durable param
 * ReviewPage/ConfirmationPage use to drop a draft from the batch submission) and returns the new
 * search string. Idempotent — a draft already marked is left as-is.
 */
export const addFailedDraftId = (search: string, draftId: string): string => {
    const params = new URLSearchParams(search);
    const failed = params.get('failedDraftIds')?.split(',').filter(Boolean) ?? [];
    if (!failed.includes(draftId)) {
        failed.push(draftId);
    }
    params.set('failedDraftIds', failed.join(','));
    return params.toString();
};

// --- Serializing answers to the SaveSQApplicationData mutation input ---
// Mirrors indeedapply/mosaic-provider-module-apply-questions' getSeedAnswers.ts: the page keys
// answers by question `name`, but they are serialized to the mutation with `question.id`.

/**
 * Numeric formats whose text answers are normalized before saving (strip separators). Must match
 * the formats `ScreenerQuestionField` renders through jspl's `NumberQuestion` (which stores a
 * localized, separator-laden value) — including CURRENCY, which that field maps to `decimal`.
 */
const NUMERIC_FORMATS = new Set<ScreenerQuestionContextQuestionFormat>([
    'CURRENCY',
    'DECIMAL',
    'INTEGER',
    'NUMERIC_TEXT',
]);

/** True when a TEXT question is serialized through numeric separator normalization. */
export const isNumericQuestion = (question: ScreenerQuestionContextQuestion): boolean =>
    Boolean(question.format && NUMERIC_FORMATS.has(question.format));

/** Strips whitespace/thousand separators and normalizes the decimal separator to ".". */
export const normalizeNumericAnswer = (
    question: ScreenerQuestionContextQuestion,
    answer: string,
): string => {
    // A locale's grouping separator can be a non-breaking space, but jobseekers type a regular
    // space, so strip all whitespace to keep serialization consistent with validation.
    let normalized = answer.replace(/\s/g, '');
    if (question.thousandSeparator) {
        normalized = normalized.split(question.thousandSeparator).join('');
    }
    if (question.decimalSeparator) {
        normalized = normalized.split(question.decimalSeparator).join('.');
    }
    return normalized;
};

/**
 * Converts the jspl DATE field's local-format value (per the employer's outputDatePattern) to the
 * ISO yyyy-MM-dd the backend requires, using jspl-foundations. Returns '' when the value is empty
 * or unparseable.
 */
const normalizeDateAnswer = (
    value: string,
    locale: string,
    question: ScreenerQuestionContextQuestion,
): string => {
    if (value === '') return '';
    // jspl-foundations parses the value with the employer's pattern when provided, falling back to
    // the locale's part order otherwise, and returns ISO yyyy-MM-dd (or '' if unparseable).
    return localDateToISO(value, locale, question.outputDatePattern ?? undefined);
};

/**
 * Builds the hierarchical answer valueList, mirroring the mosaic module's toHierarchicalAnswer:
 * the root uses `question.id`; each deeper level matches a `hierarchicalOptions` entry whose
 * condition targets the previous level's id and includes its value.
 */
const toHierarchicalAnswer = (
    question: ScreenerQuestionContextQuestion,
    values: string[],
): HierarchicalAnswerValueSmartApplyApplicationStateInput[] => {
    if (values.length === 0) return [];
    const result: HierarchicalAnswerValueSmartApplyApplicationStateInput[] = [
        { id: question.id, value: values[0] },
    ];
    values.slice(1).forEach((value) => {
        const { id, value: previousValue } = result[result.length - 1];
        const matched = question.hierarchicalOptions.filter(
            (option) =>
                option.condition.id === id && option.condition.values.indexOf(previousValue) !== -1,
        );
        if (matched.length === 0) return;
        result.push({ id: matched[0].id, value });
    });
    return result;
};

/**
 * Builds the persist URI from a file token, mirroring mosaic's `draftFileModelFromUploadedFile`:
 * the `http://localhost` host is a synthetic marker — the indapply-api-server reads only the
 * `/api/v1/files/<token>` path and resolves it in its own context. The token is a single path
 * segment, so it is percent-encoded (a no-op for the URL-safe tokens the file service issues, but it
 * keeps a stray reserved char — `/`, `?`, `#` — from breaking the URL).
 */
const fileTokenToUri = (fileToken: string): string =>
    `http://localhost/api/v1/files/${encodeURIComponent(fileToken)}`;

/** Maps a single question's answer values to the polymorphic answer input union. */
export const buildScreenerAnswerInput = (
    question: ScreenerQuestionContextQuestion,
    values: AnswerValue,
    locale: string,
): AnswerSmartApplyApplicationStateInput => {
    // FILE answers carry a richer model, but only a tokened file (from an upload or a saved answer)
    // can build a valid WebUrl. Entries without a token — in-flight/failed uploads, or name-only
    // context prefills — are dropped so the input never carries a bare filename as a WebUrl (the
    // IAFE-3026 save-breaking bug).
    if (question.type === 'FILE') {
        const files = (values as ScreenerFileAnswer[])
            .filter((file) => file.fileToken)
            .map((file) => ({ file: { uri: fileTokenToUri(file.fileToken as string) } }));
        return { multiFileAnswer: { files } };
    }
    const textValues = values as string[];
    switch (question.type) {
        case 'MULTISELECT':
            return { multiTextAnswer: { valueList: textValues.map((value) => ({ value })) } };
        case 'HIERARCHICAL':
            return {
                hierarchicalAnswer: { valueList: toHierarchicalAnswer(question, textValues) },
            };
        case 'DATE': {
            const iso = normalizeDateAnswer(textValues[0] ?? '', locale, question);
            return { textAnswer: iso === '' ? undefined : { value: iso } };
        }
        case 'TEXT': {
            const raw = textValues[0] ?? '';
            return {
                textAnswer: {
                    value: isNumericQuestion(question)
                        ? normalizeNumericAnswer(question, raw)
                        : raw,
                },
            };
        }
        case 'TEXTAREA':
        case 'SELECT':
        default:
            return { textAnswer: { value: textValues[0] ?? '' } };
    }
};

/**
 * Whether a FILE question should be sent to the save mutation. `editedNames` holds the questions the
 * jobseeker changed this session (the page's `edits`), which distinguishes an untouched prefill from
 * an explicit clear:
 *   - has a persistable (tokened) file → send it (an upload, or an untouched saved answer re-saved);
 *   - required but no token → omit (an empty required answer is rejected by the API and breaks the
 *     whole draft save; the question is held open by isMissingRequiredAnswer to prompt an upload);
 *   - optional and edited (e.g. the jobseeker removed a saved file) → send, so `files: []` clears it;
 *   - optional and untouched (a bare-filename context prefill, no token) → omit, to preserve whatever
 *     is already on the draft rather than clearing it with an empty answer.
 */
const shouldSendFileAnswer = (
    question: ScreenerQuestionContextQuestion,
    value: AnswerValue | undefined,
    editedNames: ReadonlySet<string>,
): boolean =>
    hasPersistableFileAnswer(value) || (!question.required && editedNames.has(question.name));

/**
 * Serializes the page's answer state to the mutation's `screener` array. Sends every answerable
 * question (the complete screener, never a partial set) except empty required questions, which the
 * API rejects on save-and-continue — mirroring mosaic's convertSeedAnswersToGqlInput. FILE questions
 * follow `shouldSendFileAnswer` (see there). `editedNames` is the set of question names the jobseeker
 * changed this session (the page's `edits` keys).
 */
export const buildScreenerAnswers = (
    questions: ScreenerQuestionContextQuestion[],
    answers: Partial<Record<string, AnswerValue>>,
    locale: string,
    editedNames: ReadonlySet<string> = new Set(),
): QuestionAndAnswerSmartApplyApplicationStateInput[] =>
    questions
        .filter((question) => isAnswerable(question.type))
        .filter((question) =>
            question.type === 'FILE'
                ? shouldSendFileAnswer(question, answers[question.name], editedNames)
                : !(question.required && !isAnswered(question, answers[question.name])),
        )
        .map((question) => ({
            questionId: question.id,
            answer: buildScreenerAnswerInput(question, answers[question.name] ?? [], locale),
        }));
